export const Formatify = (str: string) =>
  str.split(' ').join('').replaceAll(/\s/g, '').toLowerCase();

export const invalidNo = [
  '1000000000',
  '10000000000',
  '3000000000',
  '30000000000',
  '4000000000',
  '40000000000',
  '5000000000',
  '30000000000',
  '6000000000',
  '60000000000',
  '7000000000',
  '70000000000',
  '8000000000',
  '80000000000',
  '9000000000',
  '90000000000',
];

/**
 * Formats a phone number to a universal readable format
 * @param phoneNumber - The phone number string to format (can include country code, spaces, dashes, etc.)
 * @returns Formatted phone number string or original string if formatting fails
 */
export const formatPhoneNumber = (
  phoneNumber: string | null | undefined,
): string => {
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return phoneNumber || '';
  }

  // Remove all non-digit characters except + at the start
  const cleaned = phoneNumber.trim();
  if (!cleaned) {
    return phoneNumber;
  }

  const hasPlus = cleaned.startsWith('+');
  const digits = cleaned.replace(/\D/g, '');

  // If empty after cleaning, return original
  if (!digits) {
    return phoneNumber;
  }

  // Format based on digit count
  return formatDigits(digits, hasPlus);
};

/**
 * Helper function to format digits into phone number format
 */
const formatDigits = (digits: string, hasPlus: boolean): string => {
  const len = digits.length;
  let countryCode = '';
  let nationalNumber = digits;

  // Handle country codes
  if (hasPlus && len > 10) {
    // US/Canada: +1XXXXXXXXXX -> extract country code
    if (digits.startsWith('1') && len === 11) {
      countryCode = '+1 ';
      nationalNumber = digits.substring(1);
    } else if (len > 10) {
      // Other countries: extract country code (typically 1-3 digits)
      // Common patterns: +44, +91, +86, etc.
      const countryCodePattern = /^(\d{1,3})(\d{4,})$/;
      const match = digits.match(countryCodePattern);
      if (match && len - match[1].length >= 7) {
        countryCode = `+${match[1]} `;
        nationalNumber = match[2];
      }
    }
  } else if (!hasPlus && len === 11 && digits.startsWith('1')) {
    // Handle US numbers without + but starting with 1
    countryCode = '';
    nationalNumber = digits.substring(1);
  }

  // Format national number
  if (nationalNumber.length === 10) {
    // US format: (XXX) XXX-XXXX
    return `${countryCode}(${nationalNumber.substring(
      0,
      3,
    )}) ${nationalNumber.substring(3, 6)}-${nationalNumber.substring(6)}`;
  } else if (nationalNumber.length === 11 && nationalNumber.startsWith('1')) {
    // US with leading 1: 1XXXXXXXXXX
    return `${countryCode}(${nationalNumber.substring(
      1,
      4,
    )}) ${nationalNumber.substring(4, 7)}-${nationalNumber.substring(7)}`;
  } else if (nationalNumber.length === 7) {
    // 7 digits: XXX-XXXX
    return `${countryCode}${nationalNumber.substring(
      0,
      3,
    )}-${nationalNumber.substring(3)}`;
  } else {
    // Other lengths: try to format intelligently
    // For international numbers, add spaces every 3-4 digits
    if (nationalNumber.length > 10) {
      const formatted = nationalNumber;
      // Format in chunks
      const chunks: string[] = [];
      let remaining = formatted;

      // Take last 10 digits for main number if very long
      if (remaining.length > 10) {
        const extra = remaining.substring(0, remaining.length - 10);
        const main = remaining.substring(remaining.length - 10);
        chunks.push(extra);
        remaining = main;
      }

      // Format main number
      if (remaining.length === 10) {
        chunks.push(
          `(${remaining.substring(0, 3)}) ${remaining.substring(
            3,
            6,
          )}-${remaining.substring(6)}`,
        );
      } else {
        chunks.push(remaining);
      }

      return `${countryCode}${chunks.join(' ')}`;
    }

    // Return as-is with country code if can't format better
    return `${countryCode}${nationalNumber}`;
  }
};

/**
 * Formats a phone number to international format (same as formatPhoneNumber)
 * Alias for formatPhoneNumber for compatibility with react-phone-number-input API
 * @param phoneNumber - The phone number string to format
 * @returns Formatted phone number string
 */
export const formatPhoneNumberIntl = (
  phoneNumber: string | null | undefined,
): string => {
  return formatPhoneNumber(phoneNumber);
};
export const emailAllowed = [
  'kk@7t.co',
  'kk@riseits.com',
  'kk@itechus.net',
  'kk@sharedlabs.com',
  'preethymol.s@7t.co',
];
